import React, { useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Tooltip,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useDesignPrinciples } from "../../useQuery";
import { CustomInput } from "../../components-new/common";
import CustomDialog from "../../components-new/common/CustomDialog";
import { useNotification } from "../../hooks";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import UploadPrinciplesDialog from "./UploadPrinciplesDialog";

const DesignPrinciplesTab = () => {
  const [searchText, setSearchText] = useState("");
  const { principlesQuery, addPrincipleMutation, togglePrincipleMutation, editPrincipleMutation } =
    useDesignPrinciples(undefined, undefined, searchText);
  const notify = useNotification();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newPrincipleText, setNewPrincipleText] = useState("");
  const [newPrincipleName, setNewPrincipleName] = useState("");

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingPrinciple, setEditingPrinciple] = useState(null);
  const [editPrincipleText, setEditPrincipleText] = useState("");
  const [editPrincipleName, setEditPrincipleName] = useState("");

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const handleAddPrinciple = () => {
    if (!newPrincipleText.trim() || !newPrincipleName.trim()) return;
    addPrincipleMutation.mutate(
      {
        principleDescription: newPrincipleText,
        principleName: newPrincipleName,
      },
      {
        onSuccess: () => {
          setNewPrincipleText("");
          setNewPrincipleName("");
          setIsAddModalOpen(false);
          principlesQuery.refetch();
          notify("Design principle added successfully", "success");
        },
        onError: () => {
          notify("Failed to add design principle", "error");
        },
      },
    );
  };

  const handleDeletePrinciple = (id) => {
    togglePrincipleMutation.mutate(
      { id, isActive: false },
      {
        onSuccess: () => {
          principlesQuery.refetch();
          notify("Design principle deleted successfully", "success");
        },
        onError: () => {
          notify("Failed to delete design principle", "error");
        },
      },
    );
  };

  const handleOpenEdit = (row) => {
    setEditingPrinciple(row);
    setEditPrincipleText(row.principleDescription);
    setEditPrincipleName(row.principleName || "");
    setIsEditModalOpen(true);
  };

  const handleEditPrinciple = () => {
    if (!editPrincipleText.trim() || !editPrincipleName.trim()) return;
    editPrincipleMutation.mutate(
      {
        id: editingPrinciple.id,
        principleDescription: editPrincipleText,
        principleName: editPrincipleName,
      },
      {
        onSuccess: () => {
          setIsEditModalOpen(false);
          setEditingPrinciple(null);
          setEditPrincipleText("");
          setEditPrincipleName("");
          principlesQuery.refetch();
          notify("Design principle updated successfully", "success");
        },
        onError: () => {
          notify("Failed to update design principle", "error");
        },
      },
    );
  };

  const { data, isLoading, isError } = principlesQuery;
  const principles = data?.principles || [];

  const renderDialogContent = () => {
    return (
      <Box display="flex" flexDirection="column" gap={2} mt={1}>
        <CustomInput
          labelinput="Principle Name"
          placeholder="e.g., Text Clipping Check"
          value={newPrincipleName}
          onChange={(e) => setNewPrincipleName(e.target.value)}
          fullWidth
        />
        <CustomInput
          labelinput="Principle Description"
          placeholder="e.g., Check if any text is clipped or outside its container"
          value={newPrincipleText}
          onChange={(e) => setNewPrincipleText(e.target.value)}
          multiline
          rows={3}
          fullWidth
        />
      </Box>
    );
  };

  const renderDialogActions = () => {
    return (
      <>
        <Button
          onClick={() => setIsAddModalOpen(false)}
          color="inherit"
          disabled={addPrincipleMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAddPrinciple}
          disabled={
            addPrincipleMutation.isPending || !newPrincipleText.trim() || !newPrincipleName.trim()
          }
          sx={{ bgcolor: "#2550D1", "&:hover": { bgcolor: "#1e40a6" } }}
        >
          {addPrincipleMutation.isPending ? "Saving..." : "Save"}
        </Button>
      </>
    );
  };

  const renderEditDialogContent = () => {
    return (
      <Box display="flex" flexDirection="column" gap={2} mt={1}>
        <CustomInput
          labelinput="Principle Name"
          value={editPrincipleName}
          onChange={(e) => setEditPrincipleName(e.target.value)}
          fullWidth
        />
        <CustomInput
          labelinput="Principle Description"
          value={editPrincipleText}
          onChange={(e) => setEditPrincipleText(e.target.value)}
          multiline
          rows={3}
          fullWidth
        />
      </Box>
    );
  };

  const renderEditDialogActions = () => {
    return (
      <>
        <Button
          onClick={() => setIsEditModalOpen(false)}
          color="inherit"
          disabled={editPrincipleMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleEditPrinciple}
          disabled={
            editPrincipleMutation.isPending ||
            !editPrincipleText.trim() ||
            !editPrincipleName.trim()
          }
          sx={{ bgcolor: "#2550D1", "&:hover": { bgcolor: "#1e40a6" } }}
        >
          {editPrincipleMutation.isPending ? "Saving..." : "Update"}
        </Button>
      </>
    );
  };

  return (
    <Box pr={3} display="flex" flexDirection="column" gap={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6">Design Principles</Typography>
          <Tooltip
            title="Design principles are guidelines used to visually validate test steps during test execution. Define the rules your application's UI should follow."
            placement="right"
            arrow
          >
            <IconButton size="small" sx={{ color: "#8C9EB8" }}>
              <InfoOutlinedIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => setIsUploadModalOpen(true)}
            sx={{
              borderColor: "#2550D1",
              color: "#2550D1",
              "&:hover": { borderColor: "#1e40a6", bgcolor: "rgba(37,80,209,0.04)" },
            }}
          >
            Upload Principle
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsAddModalOpen(true)}
            sx={{ bgcolor: "#2550D1", "&:hover": { bgcolor: "#1e40a6" } }}
          >
            Add Principle
          </Button>
        </Box>
      </Box>

      <CustomInput
        placeholder="Search principles by name or principle"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        fullWidth
      />

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress size={30} />
        </Box>
      ) : isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load design principles. Please try again later.
        </Alert>
      ) : principles.length === 0 ? (
        <Box p={4} textAlign="center" border="1px dashed #E2E6F3" borderRadius={2}>
          <Typography color="text.secondary">No design principles found.</Typography>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: "1px solid #E2E6F3", borderRadius: 1 }}
        >
          <Table sx={{ minWidth: 650 }}>
            <TableHead sx={{ bgcolor: "#F5F7FA" }}>
              <TableRow>
                <TableCell width="60px">
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
                    S No.
                  </Typography>
                </TableCell>
                <TableCell width="200px">
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
                    Name
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
                    Principle
                  </Typography>
                </TableCell>
                <TableCell align="right" width="120px">
                  <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
                    Action
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {principles.map((row, index) => (
                <TableRow
                  key={row.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  hover
                >
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{row.principleName || "-"}</TableCell>
                  <TableCell>{row.principleDescription}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit Principle">
                      <IconButton onClick={() => handleOpenEdit(row)} color="primary" size="small">
                        <EditOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Principle">
                      <IconButton
                        onClick={() => handleDeletePrinciple(row.id)}
                        disabled={
                          togglePrincipleMutation.isPending &&
                          togglePrincipleMutation.variables?.id === row.id
                        }
                        color="error"
                        size="small"
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CustomDialog
        open={isAddModalOpen}
        width={530}
        onClose={() => setIsAddModalOpen(false)}
        title="Add Design Principle"
        content={renderDialogContent()}
        actions={renderDialogActions()}
      />

      <UploadPrinciplesDialog
        open={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUploaded={() => principlesQuery.refetch()}
        addPrincipleMutation={addPrincipleMutation}
      />

      <CustomDialog
        open={isEditModalOpen}
        width={530}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Design Principle"
        content={renderEditDialogContent()}
        actions={renderEditDialogActions()}
      />
    </Box>
  );
};

export default DesignPrinciplesTab;
